import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { ArrowRight } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/* ── Green Brand Palette ── */
const GREEN = "#8FEC78";
const GREEN_STRONG = "#81DD67";
const GREEN_TEXT = "rgb(13, 121, 22)";
const TEXT_PRIMARY = "rgb(0, 55, 32)";
const TEXT_BODY = "rgb(14, 15, 12)";
const TEXT_SECONDARY = "rgb(131, 146, 140)";
const TEXT_MUTED = "rgb(175, 183, 180)";
const BG_PAGE = "#FFFFFF";
const BG_SECTION = "rgba(0, 0, 0, 0.02)";
const BORDER = "rgba(0, 0, 0, 0.1)";
const ACCENT_WASH = "rgba(148, 242, 127, 0.1)";

const SERVICES = [
  { id: "prep", label: "Tax Preparation", price: "$300" },
  { id: "amendment", label: "Tax Amendment", price: "$250" },
  { id: "structure", label: "Business Structure Setup", price: "from $599" },
  { id: "bundle", label: "Structure + Tax Plan Bundle", price: "Custom" },
  { id: "consult", label: "Free Consultation", price: "Free" },
];

const DAILY_TASKS = {
  Monday: [
    { time: "8:00 AM", action: "Post 1 educational tax tip on Facebook & Instagram Stories", type: "content" },
    { time: "9:00 AM", action: "Review all weekend intake submissions & send acknowledgment emails", type: "ops" },
    { time: "10:00 AM", action: "Follow-up call/email to every pending Calendly no-show from last week", type: "sales" },
    { time: "11:30 AM", action: "Record 60-sec Reel: '1 Tax Myth Debunked This Week'", type: "content" },
    { time: "2:00 PM", action: "LinkedIn post: Why LLCs still need a formal tax plan (not just formation)", type: "content" },
    { time: "4:00 PM", action: "Check Google Business profile — respond to any new reviews or Q&A", type: "brand" },
    { time: "5:30 PM", action: "Update pipeline: log all new leads, move active clients forward", type: "ops" },
  ],
  Tuesday: [
    { time: "8:30 AM", action: "Send email blast: '5 deductions most small business owners miss every year'", type: "content" },
    { time: "10:00 AM", action: "Prep documents for today's scheduled consultations", type: "ops" },
    { time: "12:00 PM", action: "Post a client win story or transformation (anonymized or with permission)", type: "brand" },
    { time: "2:00 PM", action: "DM 10 local business owners on LinkedIn — introduce yourself, no pitch", type: "sales" },
    { time: "3:30 PM", action: "Update Calendly availability for the next 2 weeks", type: "ops" },
    { time: "5:00 PM", action: "Reply to every comment and DM on yesterday's + today's posts", type: "content" },
  ],
  Wednesday: [
    { time: "9:00 AM", action: "Host 15-min Facebook/Instagram Live: 'Ask a Tax Pro' — builds massive trust", type: "content" },
    { time: "11:00 AM", action: "Mid-week check-in email to all active clients — show you're present", type: "sales" },
    { time: "1:00 PM", action: "Write 1 SEO blog post targeting 'virtual tax prep [your city]' or 'LLC tax planning'", type: "content" },
    { time: "3:00 PM", action: "Reach out to 5 attorneys, realtors, or financial advisors about referral partnerships", type: "sales" },
    { time: "4:30 PM", action: "Audit: review website CTAs, Calendly link, and portal access — anything broken?", type: "ops" },
    { time: "6:00 PM", action: "Revenue check: where are you vs. weekly target? Identify the gap, act tomorrow", type: "ops" },
  ],
  Thursday: [
    { time: "8:00 AM", action: "Email: 'Your business structure is either saving or costing you money. Here's how to know.'", type: "content" },
    { time: "10:00 AM", action: "Create carousel: LLC vs Sole Proprietor — 6 key tax differences (repurpose for all platforms)", type: "content" },
    { time: "12:00 PM", action: "Follow up with ALL consultation no-shows from this week — second attempt", type: "sales" },
    { time: "2:00 PM", action: "Upsell call: identify 3 prep-only clients to pitch bundle package this week", type: "sales" },
    { time: "4:00 PM", action: "Record: 'Why virtual tax prep is actually MORE secure than in-person'", type: "content" },
    { time: "5:30 PM", action: "Research: 3 trending tax topics for next week's content calendar", type: "content" },
  ],
  Friday: [
    { time: "9:00 AM", action: "Send weekly summary email to all active clients — build the relationship", type: "ops" },
    { time: "10:30 AM", action: "Post: 'Weekend Read — 3 things every business owner should know before filing'", type: "content" },
    { time: "12:00 PM", action: "Review and respond to all Google, Facebook, Yelp reviews from this week", type: "brand" },
    { time: "2:00 PM", action: "Plan next week: 5 social posts, 1 email, 1 Live session — schedule all in advance", type: "ops" },
    { time: "3:30 PM", action: "Identify 1 new referral partner to add to your network next week", type: "sales" },
    { time: "5:00 PM", action: "Document 1 win from this week. Build your proof file for testimonials and case studies.", type: "brand" },
  ],
};

const STRATEGIES = [
  { title: "The Education-First Authority Play", description: "Major firms can't do this — they're too corporate. You post one genuine insight daily. Not ads. Not 'book now.' Real education. People hire who they trust, and they trust who teaches them.", roi: "Very High", effort: "Low", channel: "All Social" },
  { title: "The 'Beginning to End' Brand Promise", description: "Your name IS your differentiator. Beginning to End Tax signals full lifecycle support — from entity formation through filing through strategy. Lead every piece of marketing with this promise.", roi: "Very High", effort: "Low", channel: "All Channels" },
  { title: "The Bundle Upsell Sequence", description: "Every single tax prep client ($300) is a business structure prospect ($599+). After filing, trigger a 3-email sequence: 'Your return is filed. Now let's talk about why your current structure may be costing you.'", roi: "Very High", effort: "Medium", channel: "Email" },
  { title: "The Referral Partner Network", description: "Attorneys, realtors, mortgage brokers, financial planners — they see your exact clients first. A referral agreement with 10 of them is worth more than 6 months of ads.", roi: "Very High", effort: "Medium", channel: "LinkedIn + Direct" },
  { title: "Short-Form Video Dominance", description: "One 60-second 'tax myth busted' video can hit 10K+ views organically. H&R Block can't do this — they're a corporation. You're a person with expertise. That's your unfair advantage.", roi: "High", effort: "Medium", channel: "Reels + TikTok" },
  { title: "The 5-Email Welcome Sequence", description: "Every new intake triggers a 5-email sequence: acknowledgment → tax tip → business education → bundle offer → testimonial + booking link. Build it once, run forever.", roi: "High", effort: "Low (one-time)", channel: "Email Automation" },
];

const typeColors = { content: "#7c5cbf", sales: "#d97706", ops: "#0d7916", brand: "#2563eb" };
const typeLabels = { content: "Content", sales: "Sales", ops: "Operations", brand: "Brand" };

const INPUT_STYLE = {
  width: "100%", padding: "10px 14px", borderRadius: "8px",
  border: `1px solid ${BORDER}`, background: BG_PAGE,
  color: TEXT_BODY, fontSize: "14px", boxSizing: "border-box", outline: "none",
  fontFamily: "system-ui, sans-serif",
  transition: "border-color 0.2s",
};
const LABEL_STYLE = {
  display: "block", color: TEXT_SECONDARY, fontSize: "12px", fontWeight: "600",
  letterSpacing: "0.5px", marginBottom: "6px", textTransform: "uppercase",
  fontFamily: "system-ui, sans-serif",
};
const CARD_STYLE = {
  background: BG_PAGE, border: `1px solid ${BORDER}`, borderRadius: "12px", padding: "24px",
  boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
};

const TypeBadge = ({ type }) => (
  <span style={{
    fontSize: "10px", fontWeight: "700", letterSpacing: "0.8px", textTransform: "uppercase",
    padding: "3px 10px", borderRadius: "9999px",
    background: typeColors[type] + "15", color: typeColors[type],
    border: `1px solid ${typeColors[type]}30`,
  }}>{typeLabels[type]}</span>
);

export default function AdminDashboard() {
  const [tab, setTab] = useState("intake");
  const [form, setForm] = useState({
    name: "", email: "", phone: "", service: "Tax Preparation",
    situation: "", urgency: "Standard", referral: "Google Search", bestTime: "",
  });
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [history, setHistory] = useState([]);
  const [activeDay, setActiveDay] = useState("Monday");
  const [selectedEntry, setSelectedEntry] = useState(null);

  useEffect(() => {
    const loadHistory = async () => {
      try {
        const res = await axios.get(`${API}/generated-emails`);
        setHistory(res.data || []);
      } catch (e) {
        console.error("Failed to load history", e);
      }
    };
    loadHistory();
  }, []);

  const update = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const submitIntake = async () => {
    if (!form.name.trim() || !form.email.trim()) {
      alert("Client name and email are required.");
      return;
    }
    setLoading(true);
    setResult(null);
    try {
      const res = await axios.post(`${API}/generate-email`, {
        name: form.name, email: form.email, phone: form.phone,
        service: form.service, situation: form.situation,
        urgency: form.urgency, referral: form.referral, bestTime: form.bestTime,
      });
      const entry = {
        ...res.data, clientName: form.name, clientEmail: form.email,
        service: form.service, urgency: form.urgency, phone: form.phone,
        bestTime: form.bestTime, referral: form.referral, situation: form.situation,
        timestamp: new Date(res.data.generatedAt).toLocaleString(),
      };
      setResult(entry);
      setHistory(prev => [entry, ...prev]);
      setTab("result");
    } catch (err) {
      setResult({ error: true, message: err.response?.data?.detail || err.message });
      setTab("result");
    } finally {
      setLoading(false);
    }
  };

  const TABS = [
    { id: "intake", label: "New Intake" },
    { id: "result", label: "Response", show: !!result },
    { id: "history", label: `History${history.length ? ` (${history.length})` : ""}` },
    { id: "playbook", label: "Playbook" },
  ];

  return (
    <div style={{ fontFamily: "system-ui, -apple-system, sans-serif", background: BG_SECTION, minHeight: "100vh", color: TEXT_BODY }}>

      {/* Header */}
      <div style={{
        background: BG_PAGE, borderBottom: `1px solid ${BORDER}`, padding: "0 24px",
        position: "sticky", top: 0, zIndex: 50, backdropFilter: "blur(16px)",
        WebkitBackdropFilter: "blur(16px)",
      }}>
        <div style={{ maxWidth: "1080px", margin: "0 auto", display: "flex", justifyContent: "space-between", alignItems: "center", height: "60px", flexWrap: "wrap", gap: "12px" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
            <div style={{ width: "32px", height: "32px", borderRadius: "50%", background: `linear-gradient(135deg, ${GREEN}, ${GREEN_STRONG})`, display: "flex", alignItems: "center", justifyContent: "center" }}>
              <ArrowRight style={{ width: "16px", height: "16px", color: "white" }} />
            </div>
            <div>
              <div style={{ color: TEXT_PRIMARY, fontSize: "15px", fontWeight: "700", letterSpacing: "-0.01em" }}>Beginning to End Tax</div>
              <div style={{ color: TEXT_MUTED, fontSize: "10px", letterSpacing: "1.5px", textTransform: "uppercase" }}>Client Command Center</div>
            </div>
          </div>
          <div style={{ display: "flex", gap: "6px", flexWrap: "wrap" }}>
            {TABS.filter(t => t.show !== false).map(t => (
              <button key={t.id} onClick={() => setTab(t.id)} style={{
                padding: "7px 18px", borderRadius: "9999px", fontSize: "12px", fontWeight: "600",
                letterSpacing: "0.3px", cursor: "pointer", fontFamily: "inherit",
                border: tab === t.id ? "none" : `1px solid ${BORDER}`,
                background: tab === t.id ? `linear-gradient(135deg, ${GREEN}, ${GREEN_STRONG})` : "transparent",
                color: tab === t.id ? "white" : TEXT_SECONDARY,
                transition: "all 0.2s",
              }}>{t.label}</button>
            ))}
          </div>
        </div>
      </div>

      <div style={{ maxWidth: "1080px", margin: "0 auto", padding: "28px 24px" }}>

        {/* ── INTAKE TAB ── */}
        {tab === "intake" && (
          <div>
            <div style={{ marginBottom: "24px" }}>
              <h2 style={{ color: TEXT_PRIMARY, fontSize: "24px", margin: "0 0 6px", fontWeight: "700", letterSpacing: "-0.02em" }}>New Client Intake</h2>
              <p style={{ color: TEXT_SECONDARY, margin: 0, fontSize: "14px", lineHeight: "1.6" }}>
                Complete the form below. AI will generate a personalized professional email response — ready to send from <span style={{ color: GREEN_TEXT, fontWeight: 600 }}>info@beginningtoendtax.com</span>.
              </p>
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "20px" }}>
              {/* Left */}
              <div style={{ ...CARD_STYLE, display: "flex", flexDirection: "column", gap: "18px" }}>
                <div style={{ color: GREEN_TEXT, fontSize: "11px", letterSpacing: "1.5px", fontWeight: "700", textTransform: "uppercase", paddingBottom: "10px", borderBottom: `1px solid ${BORDER}` }}>
                  Client Information
                </div>
                <div>
                  <label style={LABEL_STYLE}>Full Name *</label>
                  <input style={INPUT_STYLE} placeholder="Jane Smith" value={form.name} onChange={e => update("name", e.target.value)} onFocus={e => e.target.style.borderColor = GREEN} onBlur={e => e.target.style.borderColor = BORDER} />
                </div>
                <div>
                  <label style={LABEL_STYLE}>Email Address *</label>
                  <input style={INPUT_STYLE} placeholder="jane@example.com" type="email" value={form.email} onChange={e => update("email", e.target.value)} onFocus={e => e.target.style.borderColor = GREEN} onBlur={e => e.target.style.borderColor = BORDER} />
                </div>
                <div>
                  <label style={LABEL_STYLE}>Phone Number</label>
                  <input style={INPUT_STYLE} placeholder="(555) 000-0000" value={form.phone} onChange={e => update("phone", e.target.value)} onFocus={e => e.target.style.borderColor = GREEN} onBlur={e => e.target.style.borderColor = BORDER} />
                </div>
                <div>
                  <label style={LABEL_STYLE}>Best Time to Reach</label>
                  <input style={INPUT_STYLE} placeholder="e.g., Weekdays after 3 PM" value={form.bestTime} onChange={e => update("bestTime", e.target.value)} onFocus={e => e.target.style.borderColor = GREEN} onBlur={e => e.target.style.borderColor = BORDER} />
                </div>
                <div>
                  <label style={LABEL_STYLE}>How Did They Find Us?</label>
                  <select style={INPUT_STYLE} value={form.referral} onChange={e => update("referral", e.target.value)}>
                    {["Google Search", "Social Media", "Referral from Friend/Family", "LinkedIn", "YouTube/Reels", "beginningtoendtax.com", "Other"].map(r => <option key={r} value={r}>{r}</option>)}
                  </select>
                </div>
              </div>

              {/* Right */}
              <div style={{ ...CARD_STYLE, display: "flex", flexDirection: "column", gap: "18px" }}>
                <div style={{ color: GREEN_TEXT, fontSize: "11px", letterSpacing: "1.5px", fontWeight: "700", textTransform: "uppercase", paddingBottom: "10px", borderBottom: `1px solid ${BORDER}` }}>
                  Service & Situation
                </div>
                <div>
                  <label style={LABEL_STYLE}>Service Requested *</label>
                  <select style={INPUT_STYLE} value={form.service} onChange={e => update("service", e.target.value)}>
                    {SERVICES.map(s => <option key={s.id} value={s.label}>{s.label}</option>)}
                  </select>
                  <div style={{ marginTop: "6px", fontSize: "13px", color: GREEN_TEXT, fontWeight: "600" }}>
                    {SERVICES.find(s => s.label === form.service)?.price}
                  </div>
                </div>
                <div>
                  <label style={LABEL_STYLE}>Urgency Level</label>
                  <select style={INPUT_STYLE} value={form.urgency} onChange={e => update("urgency", e.target.value)}>
                    {["Standard (7–10 days)", "Priority (3–5 days)", "Rush (1–2 days)"].map(u => <option key={u} value={u}>{u}</option>)}
                  </select>
                </div>
                <div style={{ flex: 1 }}>
                  <label style={LABEL_STYLE}>Their Situation / Notes</label>
                  <textarea
                    style={{ ...INPUT_STYLE, minHeight: "140px", resize: "vertical" }}
                    placeholder="Brief description of their tax situation, business type, questions, or any special circumstances..."
                    value={form.situation}
                    onChange={e => update("situation", e.target.value)}
                    onFocus={e => e.target.style.borderColor = GREEN}
                    onBlur={e => e.target.style.borderColor = BORDER}
                  />
                </div>
              </div>
            </div>

            <div style={{ marginTop: "24px", display: "flex", alignItems: "center", gap: "16px" }}>
              <button
                onClick={submitIntake}
                disabled={loading}
                style={{
                  padding: "14px 36px", borderRadius: "9999px", border: "none",
                  background: loading ? "#e5e7eb" : `linear-gradient(135deg, ${GREEN}, ${GREEN_STRONG})`,
                  color: loading ? TEXT_SECONDARY : "white", fontSize: "15px", fontWeight: "700",
                  cursor: loading ? "not-allowed" : "pointer", fontFamily: "inherit",
                  boxShadow: loading ? "none" : "0 2px 8px rgba(148,242,127,0.3)",
                  transition: "all 0.2s",
                }}
              >
                {loading ? "Generating Response..." : "Submit & Generate Email"}
              </button>
              {loading && (
                <div style={{ color: TEXT_SECONDARY, fontSize: "13px" }}>
                  Drafting a personalized email for {form.name || "your client"}...
                </div>
              )}
            </div>
          </div>
        )}

        {/* ── RESULT TAB ── */}
        {tab === "result" && result && (
          <div>
            {result.error ? (
              <div style={{ ...CARD_STYLE, borderColor: "#fca5a5" }}>
                <div style={{ color: "#dc2626", fontWeight: "700", marginBottom: "8px" }}>Error Generating Response</div>
                <div style={{ color: TEXT_SECONDARY, fontSize: "13px" }}>{result.message}</div>
                <button onClick={() => setTab("intake")} style={{ marginTop: "16px", padding: "8px 20px", background: BG_SECTION, border: `1px solid ${BORDER}`, color: TEXT_PRIMARY, borderRadius: "9999px", cursor: "pointer", fontFamily: "inherit", fontSize: "13px" }}>← Back to Intake</button>
              </div>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
                {/* Status bar */}
                <div style={{
                  ...CARD_STYLE, background: "rgba(148,242,127,0.06)", borderColor: "rgba(148,242,127,0.3)",
                  display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: "12px",
                }}>
                  <div>
                    <div style={{ color: GREEN_TEXT, fontWeight: "700", fontSize: "15px", display: "flex", alignItems: "center", gap: "6px" }}>
                      <span style={{ width: "8px", height: "8px", borderRadius: "50%", background: GREEN_TEXT, display: "inline-block" }} />
                      Email Generated — Ready to send to {result.clientEmail}
                    </div>
                    <div style={{ color: TEXT_SECONDARY, fontSize: "12px", marginTop: "3px" }}>
                      For: {result.clientName} ({result.clientEmail}) · {result.timestamp}
                    </div>
                  </div>
                  <div style={{ display: "flex", gap: "8px" }}>
                    <span style={{ padding: "4px 14px", background: ACCENT_WASH, color: GREEN_TEXT, border: `1px solid rgba(148,242,127,0.3)`, borderRadius: "9999px", fontSize: "11px", fontWeight: "700" }}>{result.service}</span>
                    <span style={{ padding: "4px 14px", background: "rgba(217,119,6,0.08)", color: "#d97706", border: `1px solid rgba(217,119,6,0.2)`, borderRadius: "9999px", fontSize: "11px", fontWeight: "700" }}>{result.urgency}</span>
                  </div>
                </div>

                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "16px" }}>
                  {/* Email preview */}
                  <div style={CARD_STYLE}>
                    <div style={{ color: GREEN_TEXT, fontSize: "11px", letterSpacing: "1.5px", fontWeight: "700", textTransform: "uppercase", marginBottom: "16px" }}>Email Draft</div>
                    <div style={{ marginBottom: "14px" }}>
                      <div style={{ color: TEXT_MUTED, fontSize: "11px", textTransform: "uppercase", letterSpacing: "1px", marginBottom: "4px" }}>Subject Line</div>
                      <div style={{ color: TEXT_PRIMARY, fontSize: "16px", fontWeight: "600" }}>{result.subject || "—"}</div>
                    </div>
                    <div style={{ marginBottom: "14px" }}>
                      <div style={{ color: TEXT_MUTED, fontSize: "11px", textTransform: "uppercase", letterSpacing: "1px", marginBottom: "4px" }}>Preview</div>
                      <div style={{ color: TEXT_SECONDARY, fontSize: "13px", lineHeight: "1.6", fontStyle: "italic" }}>{result.preview || "—"}</div>
                    </div>
                    {result.fullEmail && (
                      <div style={{ marginBottom: "14px" }}>
                        <div style={{ color: TEXT_MUTED, fontSize: "11px", textTransform: "uppercase", letterSpacing: "1px", marginBottom: "8px" }}>Full Email</div>
                        <div style={{ color: TEXT_BODY, fontSize: "13px", lineHeight: "1.8", whiteSpace: "pre-wrap", background: BG_SECTION, borderRadius: "8px", padding: "16px", maxHeight: "320px", overflowY: "auto", border: `1px solid ${BORDER}` }}>
                          {result.fullEmail}
                        </div>
                      </div>
                    )}
                    {result.nextSteps?.length > 0 && (
                      <div>
                        <div style={{ color: TEXT_MUTED, fontSize: "11px", textTransform: "uppercase", letterSpacing: "1px", marginBottom: "10px" }}>Client's Next Steps</div>
                        {result.nextSteps.map((step, i) => (
                          <div key={i} style={{ display: "flex", gap: "10px", marginBottom: "8px", alignItems: "flex-start" }}>
                            <div style={{ color: "white", fontSize: "10px", fontWeight: "700", minWidth: "20px", height: "20px", borderRadius: "50%", background: `linear-gradient(135deg, ${GREEN}, ${GREEN_STRONG})`, display: "flex", alignItems: "center", justifyContent: "center", marginTop: "1px", flexShrink: 0 }}>{i + 1}</div>
                            <div style={{ color: TEXT_BODY, fontSize: "13px", lineHeight: "1.5" }}>{step}</div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Right side */}
                  <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
                    {result.proInsight && (
                      <div style={{ ...CARD_STYLE, background: "rgba(148,242,127,0.04)", borderColor: "rgba(148,242,127,0.25)" }}>
                        <div style={{ color: GREEN_TEXT, fontSize: "11px", letterSpacing: "1.5px", fontWeight: "700", textTransform: "uppercase", marginBottom: "12px" }}>Pro Insight for This Client</div>
                        <div style={{ color: TEXT_PRIMARY, fontSize: "14px", lineHeight: "1.7", fontStyle: "italic" }}>"{result.proInsight}"</div>
                        <div style={{ color: TEXT_MUTED, fontSize: "11px", marginTop: "12px" }}>Lead with this in your consultation — it signals expertise before they say a word.</div>
                      </div>
                    )}
                    <div style={CARD_STYLE}>
                      <div style={{ color: GREEN_TEXT, fontSize: "11px", letterSpacing: "1.5px", fontWeight: "700", textTransform: "uppercase", marginBottom: "12px" }}>Client Details</div>
                      {[
                        ["Phone", result.phone || "Not provided"],
                        ["Best Time", result.bestTime || "Any time"],
                        ["Found Us Via", result.referral],
                        ["Situation", result.situation || "Not specified"],
                      ].map(([k, v]) => (
                        <div key={k} style={{ display: "flex", justifyContent: "space-between", padding: "8px 0", borderBottom: `1px solid ${BORDER}`, fontSize: "13px" }}>
                          <span style={{ color: TEXT_SECONDARY }}>{k}</span>
                          <span style={{ color: TEXT_BODY, maxWidth: "58%", textAlign: "right" }}>{v}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                <div style={{ display: "flex", gap: "12px", marginTop: "4px" }}>
                  <button onClick={() => {
                    setForm({ name: "", email: "", phone: "", service: "Tax Preparation", situation: "", urgency: "Standard", referral: "Google Search", bestTime: "" });
                    setTab("intake");
                  }} style={{ padding: "10px 24px", background: "transparent", border: `1px solid ${BORDER}`, color: TEXT_PRIMARY, borderRadius: "9999px", cursor: "pointer", fontSize: "13px", fontWeight: "600", fontFamily: "inherit" }}>
                    New Intake
                  </button>
                  <button onClick={() => setTab("history")} style={{ padding: "10px 24px", background: BG_SECTION, border: `1px solid ${BORDER}`, color: TEXT_BODY, borderRadius: "9999px", cursor: "pointer", fontSize: "13px", fontFamily: "inherit" }}>
                    View All History ({history.length})
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* ── HISTORY TAB ── */}
        {tab === "history" && (
          <div>
            <div style={{ marginBottom: "24px" }}>
              <h2 style={{ color: TEXT_PRIMARY, fontSize: "24px", margin: "0 0 6px", fontWeight: "700" }}>Intake History</h2>
              <p style={{ color: TEXT_SECONDARY, margin: 0, fontSize: "14px" }}>{history.length} submission{history.length !== 1 ? "s" : ""} total</p>
            </div>
            {history.length === 0 ? (
              <div style={{ ...CARD_STYLE, textAlign: "center", padding: "48px", color: TEXT_SECONDARY }}>No submissions yet. Submit an intake form to see it here.</div>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
                {history.map((entry, idx) => (
                  <div
                    key={entry.id || idx}
                    onClick={() => setSelectedEntry(selectedEntry?.id === entry.id ? null : entry)}
                    style={{ ...CARD_STYLE, cursor: "pointer", borderColor: selectedEntry?.id === entry.id ? GREEN : BORDER, transition: "border-color 0.2s, box-shadow 0.2s", boxShadow: selectedEntry?.id === entry.id ? `0 0 0 1px ${GREEN}` : "0 1px 3px rgba(0,0,0,0.06)" }}
                  >
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "8px" }}>
                      <div>
                        <span style={{ color: TEXT_PRIMARY, fontWeight: "600", fontSize: "15px" }}>{entry.clientName}</span>
                        <span style={{ color: TEXT_SECONDARY, fontSize: "12px", marginLeft: "12px" }}>{entry.clientEmail}</span>
                      </div>
                      <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
                        <span style={{ color: GREEN_TEXT, fontSize: "12px", fontWeight: "600" }}>{entry.service || entry.intakeData?.service}</span>
                        <span style={{ color: TEXT_MUTED, fontSize: "11px" }}>{entry.generatedAt ? new Date(entry.generatedAt).toLocaleString() : ""}</span>
                        <span style={{ color: GREEN_TEXT, fontSize: "11px", fontWeight: "700", display: "flex", alignItems: "center", gap: "3px" }}>
                          <span style={{ width: "6px", height: "6px", borderRadius: "50%", background: GREEN }}></span> Generated
                        </span>
                      </div>
                    </div>
                    {selectedEntry?.id === entry.id && (
                      <div style={{ marginTop: "16px", paddingTop: "16px", borderTop: `1px solid ${BORDER}` }}>
                        <div style={{ color: TEXT_MUTED, fontSize: "11px", textTransform: "uppercase", letterSpacing: "1px", marginBottom: "4px" }}>Email Subject</div>
                        <div style={{ color: TEXT_PRIMARY, fontSize: "14px", fontWeight: "600", marginBottom: "12px" }}>{entry.subject || "—"}</div>
                        {entry.proInsight && (
                          <div style={{ background: ACCENT_WASH, border: `1px solid rgba(148,242,127,0.25)`, borderRadius: "8px", padding: "12px 16px" }}>
                            <div style={{ color: GREEN_TEXT, fontSize: "11px", fontWeight: "700", letterSpacing: "1px", marginBottom: "4px" }}>PRO INSIGHT</div>
                            <div style={{ color: TEXT_PRIMARY, fontSize: "13px", fontStyle: "italic", lineHeight: "1.6" }}>{entry.proInsight}</div>
                          </div>
                        )}
                        {entry.fullEmail && (
                          <div style={{ marginTop: "12px", color: TEXT_BODY, fontSize: "12px", lineHeight: "1.7", whiteSpace: "pre-wrap", background: BG_SECTION, borderRadius: "8px", padding: "14px", maxHeight: "200px", overflowY: "auto", border: `1px solid ${BORDER}` }}>
                            {entry.fullEmail}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ── PLAYBOOK TAB ── */}
        {tab === "playbook" && (
          <div>
            <div style={{ marginBottom: "28px" }}>
              <h2 style={{ color: TEXT_PRIMARY, fontSize: "24px", margin: "0 0 6px", fontWeight: "700" }}>Brand Scaling Playbook</h2>
              <p style={{ color: TEXT_SECONDARY, margin: 0, fontSize: "14px", lineHeight: "1.6" }}>
                Daily execution plan + competitive strategies to grow Beginning to End Tax into a nationally recognized brand.
              </p>
            </div>

            {/* Day selector */}
            <div style={{ display: "flex", gap: "6px", marginBottom: "20px", flexWrap: "wrap" }}>
              {Object.keys(DAILY_TASKS).map(day => (
                <button key={day} onClick={() => setActiveDay(day)} style={{
                  padding: "8px 20px", borderRadius: "9999px", fontSize: "13px", fontWeight: "600",
                  cursor: "pointer", fontFamily: "inherit",
                  border: activeDay === day ? "none" : `1px solid ${BORDER}`,
                  background: activeDay === day ? `linear-gradient(135deg, ${GREEN}, ${GREEN_STRONG})` : BG_PAGE,
                  color: activeDay === day ? "white" : TEXT_SECONDARY,
                  transition: "all 0.2s",
                  boxShadow: activeDay === day ? "0 2px 8px rgba(148,242,127,0.3)" : "0 1px 2px rgba(0,0,0,0.05)",
                }}>{day}</button>
              ))}
            </div>

            {/* Daily tasks */}
            <div style={{ ...CARD_STYLE, marginBottom: "28px" }}>
              <div style={{ color: GREEN_TEXT, fontSize: "11px", letterSpacing: "1.5px", fontWeight: "700", textTransform: "uppercase", marginBottom: "18px" }}>
                {activeDay} — Daily Action Plan
              </div>
              <div style={{ display: "flex", flexDirection: "column" }}>
                {DAILY_TASKS[activeDay].map((task, i) => (
                  <div key={i} style={{
                    display: "grid", gridTemplateColumns: "80px 1fr auto", gap: "16px",
                    padding: "14px 0", alignItems: "center",
                    borderBottom: i < DAILY_TASKS[activeDay].length - 1 ? `1px solid ${BORDER}` : "none",
                  }}>
                    <div style={{ color: TEXT_MUTED, fontSize: "12px", fontWeight: "500" }}>{task.time}</div>
                    <div style={{ color: TEXT_BODY, fontSize: "13px", lineHeight: "1.5" }}>{task.action}</div>
                    <TypeBadge type={task.type} />
                  </div>
                ))}
              </div>
              <div style={{ marginTop: "16px", display: "flex", gap: "16px", flexWrap: "wrap" }}>
                {Object.entries(typeColors).map(([k, c]) => (
                  <div key={k} style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                    <div style={{ width: "8px", height: "8px", borderRadius: "50%", background: c }} />
                    <span style={{ color: TEXT_MUTED, fontSize: "11px" }}>{typeLabels[k]}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Competitive strategies */}
            <div style={{ color: GREEN_TEXT, fontSize: "11px", letterSpacing: "1.5px", fontWeight: "700", textTransform: "uppercase", marginBottom: "16px" }}>
              6 Strategies That Compete Against Major Firms
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "16px", marginBottom: "28px" }}>
              {STRATEGIES.map((s, i) => (
                <div key={i} style={{ ...CARD_STYLE }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "10px" }}>
                    <div style={{ color: TEXT_PRIMARY, fontSize: "15px", fontWeight: "700", lineHeight: "1.3", flex: 1, marginRight: "10px" }}>{s.title}</div>
                    <span style={{ color: GREEN_TEXT, fontSize: "10px", fontWeight: "700", background: ACCENT_WASH, padding: "3px 10px", borderRadius: "9999px", whiteSpace: "nowrap", border: "1px solid rgba(148,242,127,0.2)" }}>ROI: {s.roi}</span>
                  </div>
                  <div style={{ color: TEXT_SECONDARY, fontSize: "13px", lineHeight: "1.7", marginBottom: "12px" }}>{s.description}</div>
                  <div style={{ display: "flex", justifyContent: "space-between", fontSize: "12px" }}>
                    <span style={{ color: TEXT_MUTED }}>Effort: <span style={{ color: TEXT_BODY, fontWeight: 500 }}>{s.effort}</span></span>
                    <span style={{ color: TEXT_MUTED }}>Channel: <span style={{ color: GREEN_TEXT, fontWeight: 500 }}>{s.channel}</span></span>
                  </div>
                </div>
              ))}
            </div>

            {/* Value ladder */}
            <div style={{ ...CARD_STYLE, background: "linear-gradient(135deg, rgba(148,242,127,0.04) 0%, rgba(148,242,127,0.01) 100%)", borderColor: "rgba(148,242,127,0.2)" }}>
              <div style={{ color: GREEN_TEXT, fontSize: "11px", letterSpacing: "1.5px", fontWeight: "700", textTransform: "uppercase", marginBottom: "18px" }}>
                The Beginning to End Tax Value Ladder — Move Every Client Up
              </div>
              <div style={{ display: "flex", gap: "12px", alignItems: "stretch" }}>
                {[
                  { label: "Free Consult", sub: "Entry point", price: "$0", color: "#0d7916" },
                  { label: "Tax Prep", sub: "Core service", price: "$300", color: "#2563eb" },
                  { label: "Amendment", sub: "Prior year fix", price: "$250", color: "#7c5cbf" },
                  { label: "Biz Structure", sub: "Foundation", price: "$599+", color: GREEN_TEXT },
                  { label: "Bundle", sub: "Max value", price: "Custom", color: "#dc2626" },
                ].map((step, i) => (
                  <div key={i} style={{ flex: 1 }}>
                    <div style={{
                      background: step.color + "0A", border: `1px solid ${step.color}25`,
                      borderRadius: "10px", padding: "14px 10px", textAlign: "center",
                    }}>
                      <div style={{ color: step.color, fontSize: "14px", fontWeight: "700", marginBottom: "4px" }}>{step.price}</div>
                      <div style={{ color: TEXT_PRIMARY, fontSize: "12px", fontWeight: "600", marginBottom: "2px" }}>{step.label}</div>
                      <div style={{ color: TEXT_MUTED, fontSize: "10px" }}>{step.sub}</div>
                    </div>
                  </div>
                ))}
              </div>
              <div style={{ color: TEXT_SECONDARY, fontSize: "12px", marginTop: "16px", textAlign: "center", fontStyle: "italic" }}>
                Beginning to End — that's not just a name. It's your promise. Every client starts somewhere and grows with you.
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
